import { LexicalEditor, $getSelection, $isRangeSelection, $insertNodes, LexicalNode } from 'lexical';
import { $generateHtmlFromNodes } from '@lexical/html';
import { datadogLogs } from '@datadog/browser-logs';

export const MAX_BODY_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB

export const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

export const getStringByteSize = (str: string): number => {
  return new Blob([str]).size;
};

export const getBase64Size = (base64String: string): number => {
  const base64 = base64String.split(',')[1] || base64String;
  const padding = (base64.match(/=+$/) || [''])[0].length;
  return Math.floor((base64.length * 3) / 4) - padding;
};

export interface InsertImageOptions {
  imageType?: string;
  onBodySizeExceeded?: (exceeded: boolean) => void;
}

export const tryInsertImage = (
  editor: LexicalEditor,
  base64Result: string,
  createImageNode: (src: string) => LexicalNode,
  options: InsertImageOptions = {}
): boolean => {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) {
    return false;
  }

  const newImageSize = getBase64Size(base64Result);
  const html = $generateHtmlFromNodes(editor);
  const currentBodySize = getStringByteSize(html);
  const projectedBodySize = currentBodySize + base64Result.length;

  if (projectedBodySize > MAX_BODY_SIZE_BYTES) {
    datadogLogs.logger.warn('Image not inserted: would exceed 5 MB body limit', {
      imageType: options.imageType,
      newImageSize: formatBytes(newImageSize),
      currentBodySize: formatBytes(currentBodySize),
      projectedBodySize: formatBytes(projectedBodySize)
    });
    options.onBodySizeExceeded?.(true);
    return false;
  }

  const imageNode = createImageNode(base64Result);
  $insertNodes([imageNode]);
  return true;
};
